import { apiBase } from "@/const";
import { requiredAuth } from "@/helpers";
import axios from "axios";

import type { AxiosResponse } from "axios";
import type {
    StorageGMBMediaPostResponse,
    MybusinessMediaItem,
    EntitiesGbpResponseMybusinessListMediaItemsResponse,
    EntitiesGbpResponseMybusinessMediaItem,
} from "@/types/ls-api";
import type { OperationLogParams } from "@/routes/operation-log";
import type { MediaFile } from "@/components/root/contents/gmbmedia/MediaColumn";
import type { definitions } from "@/types/swagger";
type MediaItem = definitions["mybusiness.MediaItem"];

// バッチ処理用の型定義
export type MediaItemRequest = {
    sourceUrl: string;
    /** GMB API の mediaFormat と同値（PHOTO / VIDEO） */
    mediaFormat?: string;
    locationAssociation?: { category: string };
    name?: string;
    thumbnailUrl?: string;
};

export type MediaResult = {
    poiId: number;
    status: "success" | "failed";
    mediaItem?: any;
    error?: string;
};

export type BulkMediaResponse = {
    total: number;
    success: number;
    failed: number;
    results: MediaResult[];
};

export type DeleteMediaItemRequest = {
    poiId: number;
    mediaName: string;
};

export type BulkDeleteMediaResponse = {
    total: number;
    success: number;
    failed: number;
    results: MediaResult[];
};

export const api = {
    gmbapiinit,
    listMediaItem,
    patchMediaItem,
    putMediaItemBulk,
    deleteMediaItemBulk,
    uploadMedia,
    putMedia,
};

class RequestError extends Error {
    response: AxiosResponse;
    constructor(message: string, response: AxiosResponse) {
        super(message);
        this.response = response;
    }
}

// FIXME: すべてのAPIをGmbMediaEdit.vueから移植しているわけではないので、必要に応じて追加してください

async function gmbapiinit(poiGroupID: number): Promise<boolean> {
    try {
        const url = `${apiBase}/companies/${poiGroupID}/gmbapiinit`;
        await requiredAuth("get", url);
        return true;
    } catch (e) {
        return false;
    }
}

async function listMediaItem(
    poiGroupID: number,
    poiID: number,
    pageSize: number,
    pageToken: string
): Promise<EntitiesGbpResponseMybusinessListMediaItemsResponse> {
    const url = `${apiBase}/companies/${poiGroupID}/stores/${poiID}/gmb/media`;
    const res = await requiredAuth<EntitiesGbpResponseMybusinessListMediaItemsResponse>(
        "get",
        url,
        {
            pageToken,
            pageSize,
        }
    );
    return res.data;
}

async function patchMediaItem(
    poiGroupID: number,
    poiID: number,
    item: MediaItem,
    oplog: OperationLogParams
): Promise<EntitiesGbpResponseMybusinessMediaItem> {
    const url = `${apiBase}/companies/${poiGroupID}/stores/${poiID}/gmb/media`;
    const res = await requiredAuth<EntitiesGbpResponseMybusinessMediaItem>(
        "patch",
        url,
        oplog,
        item
    );
    return res.data;
}

async function putMediaItemBulk(
    poiGroupID: number,
    poiIDs: number[],
    item: MediaItemRequest,
    oplog: OperationLogParams
): Promise<BulkMediaResponse> {
    const url = `${apiBase}/companies/${poiGroupID}/stores/gmb/media`;
    const res = await requiredAuth<BulkMediaResponse>("post", url, oplog, {
        mediaItem: item,
        poiIDs: poiIDs,
    });
    return res.data;
}

/**
 * バッチ処理で失敗した店舗のみ再リクエスト（最大3回リトライ、合計4回実行）
 * @param poiGroupID 店舗グループID
 * @param poiIDs 店舗IDの配列
 * @param mediaItem メディアアイテム
 * @param oplog オペレーションログパラメータ
 * @param onProgress 進捗コールバック（オプション）
 * @returns バッチ処理結果
 */
export async function putMediaItemBulkWithRetry(
    poiGroupID: number,
    poiIDs: number[],
    mediaItem: MediaItemRequest,
    oplog: OperationLogParams,
    onProgress?: (processed: number, total: number) => void
): Promise<BulkMediaResponse> {
    const sleep = (msec: number) => new Promise((resolve) => setTimeout(resolve, msec));
    const MAX_RETRIES = 3; // 最大3回リトライ（合計4回実行）
    const BATCH_SIZE = 100; // 1リクエストあたり最大100店舗
    const CONCURRENT_REQUESTS = 3; // 並行リクエスト数（サーバー側のレート制限を考慮）

    // リトライすべきエラーかどうかを判定
    // サーバー側のエラーメッセージから判定（GMB APIのエラーはリトライ対象）
    const shouldRetry = (error: string | undefined): boolean => {
        if (!error) return false;
        // 以下のエラーはリトライ対象外（永続的なエラー）
        const nonRetryableErrors = [
            "店舗が見つかりませんでした",
            "権限が不足しています",
            "画像変換失敗", // マルチフレーム画像の変換失敗は永続的
        ];
        if (nonRetryableErrors.some((msg) => error.includes(msg))) {
            return false;
        }
        // その他のエラーはリトライ対象（GMB APIの一時的なエラーなど）
        return true;
    };

    // 店舗IDを100店舗ずつに分割
    const batches: number[][] = [];
    for (let i = 0; i < poiIDs.length; i += BATCH_SIZE) {
        batches.push(poiIDs.slice(i, i + BATCH_SIZE));
    }

    const allResults: MediaResult[] = [];

    // 並行リクエストでバッチを処理
    const processBatch = async (batch: number[]): Promise<MediaResult[]> => {
        const batchResults: MediaResult[] = [];
        let remainingPoiIDs = [...batch];
        let retryCount = 0;

        while (remainingPoiIDs.length > 0 && retryCount <= MAX_RETRIES) {
            try {
                // バッチAPIを呼び出す
                const res = await putMediaItemBulk(poiGroupID, remainingPoiIDs, mediaItem, oplog);

                // 成功した店舗と失敗した店舗を分ける
                const failedPoiIDs: number[] = [];
                const successPoiIDs = new Set<number>();

                for (const result of res.results) {
                    if (result.status === "success") {
                        // 成功した店舗は記録（リトライで成功した場合、以前の失敗結果を上書き）
                        successPoiIDs.add(result.poiId);
                        // 以前の失敗結果を削除
                        const existingIndex = batchResults.findIndex(
                            (r) => r.poiId === result.poiId && r.status === "failed"
                        );
                        if (existingIndex >= 0) {
                            batchResults.splice(existingIndex, 1);
                        }
                        // 成功結果を追加
                        batchResults.push(result);
                    } else if (result.status === "failed") {
                        if (shouldRetry(result.error)) {
                            failedPoiIDs.push(result.poiId);
                            // リトライ対象の失敗結果は、リトライが成功するまで保持しない
                            // （リトライが成功した場合は成功結果で上書きされる）
                        } else {
                            // リトライ対象外の失敗結果は即座に追加
                            batchResults.push(result);
                        }
                    }
                }

                // 失敗した店舗がなければ終了
                if (failedPoiIDs.length === 0) {
                    break;
                }

                // リトライ上限に達したら終了
                if (retryCount >= MAX_RETRIES) {
                    // リトライ上限に達した場合、残っている失敗結果を追加
                    for (const poiID of failedPoiIDs) {
                        const result = res.results.find((r) => r.poiId === poiID);
                        if (result && result.status === "failed") {
                            batchResults.push(result);
                        }
                    }
                    break;
                }

                // 失敗した店舗のみで再試行
                remainingPoiIDs = failedPoiIDs;
                retryCount++;

                // リトライ間隔（指数バックオフ: 1, 2, 4秒）
                const delay = Math.min(1000 * Math.pow(2, retryCount - 1), 4000);
                await sleep(delay);
            } catch (e) {
                // 504エラー（タイムアウト）の場合はリトライ対象
                const isTimeout = axios.isAxiosError(e) && e.response?.status === 504;
                if (isTimeout && retryCount < MAX_RETRIES) {
                    // タイムアウトの場合はリトライ
                    retryCount++;
                    const delay = Math.min(1000 * Math.pow(2, retryCount - 1), 4000);
                    await sleep(delay);
                    continue;
                }
                // その他のエラーまたはリトライ上限に達した場合は全店舗を失敗として記録
                console.error("バッチ処理でエラーが発生しました:", e);
                for (const poiID of remainingPoiIDs) {
                    batchResults.push({
                        poiId: poiID,
                        status: "failed",
                        error: e instanceof Error ? e.message : String(e),
                    });
                }
                break;
            }
        }
        return batchResults;
    };

    // 処理開始時に進捗を0で表示
    if (onProgress) {
        onProgress(0, poiIDs.length);
    }

    // 並行リクエストを制御（最大CONCURRENT_REQUESTS並列）
    // 進捗が巻き戻らないように、完了したバッチ数で管理
    let completedBatches = 0;
    const processBatchesInParallel = async (): Promise<void> => {
        const results: MediaResult[][] = [];
        for (let i = 0; i < batches.length; i += CONCURRENT_REQUESTS) {
            const chunk = batches.slice(i, i + CONCURRENT_REQUESTS);
            const chunkResults = await Promise.all(
                chunk.map(async (batch) => {
                    const batchResults = await processBatch(batch);
                    // バッチ完了時に進捗を更新（常に増加するように）
                    completedBatches++;
                    if (onProgress) {
                        const processedCount = completedBatches * BATCH_SIZE;
                        onProgress(Math.min(processedCount, poiIDs.length), poiIDs.length);
                    }
                    return batchResults;
                })
            );
            results.push(...chunkResults);
        }
        // 結果を統合
        for (const batchResults of results) {
            allResults.push(...batchResults);
        }
    };

    await processBatchesInParallel();

    // 最終結果をまとめる
    const successCount = allResults.filter((r) => r.status === "success").length;
    const failedCount = allResults.filter((r) => r.status === "failed").length;

    return {
        total: poiIDs.length,
        success: successCount,
        failed: failedCount,
        results: allResults,
    };
}

async function deleteMediaItemBulk(
    poiGroupID: number,
    deleteItems: DeleteMediaItemRequest[],
    oplog: OperationLogParams
): Promise<BulkDeleteMediaResponse> {
    const url = `${apiBase}/companies/${poiGroupID}/stores/gmb/media/delete`;
    const res = await requiredAuth<BulkDeleteMediaResponse>("post", url, oplog, {
        items: deleteItems,
    });
    return res.data;
}

/**
 * バッチ削除処理で失敗した店舗のみ再リクエスト（最大3回リトライ、合計4回実行）
 * @param poiGroupID 店舗グループID
 * @param deleteItems 削除対象のアイテム配列
 * @param oplog オペレーションログパラメータ
 * @param onProgress 進捗コールバック（オプション）
 * @returns バッチ処理結果
 */
export async function deleteMediaItemBulkWithRetry(
    poiGroupID: number,
    deleteItems: DeleteMediaItemRequest[],
    oplog: OperationLogParams,
    onProgress?: (processed: number, total: number) => void
): Promise<BulkDeleteMediaResponse> {
    const sleep = (msec: number) => new Promise((resolve) => setTimeout(resolve, msec));
    const MAX_RETRIES = 3; // 最大3回リトライ（合計4回実行）
    const BATCH_SIZE = 100; // 1リクエストあたり最大100件
    const CONCURRENT_REQUESTS = 3; // 並行リクエスト数（サーバー側のレート制限を考慮）

    // リトライすべきエラーかどうかを判定
    const shouldRetry = (error: string | undefined): boolean => {
        if (!error) return false;
        // 以下のエラーはリトライ対象外（永続的なエラー）
        const nonRetryableErrors = [
            "店舗が見つかりませんでした",
            "権限が不足しています",
            "メディアが見つかりませんでした",
        ];
        if (nonRetryableErrors.some((msg) => error.includes(msg))) {
            return false;
        }
        // その他のエラーはリトライ対象（GMB APIの一時的なエラーなど）
        return true;
    };

    // 削除アイテムを100件ずつに分割
    const batches: DeleteMediaItemRequest[][] = [];
    for (let i = 0; i < deleteItems.length; i += BATCH_SIZE) {
        batches.push(deleteItems.slice(i, i + BATCH_SIZE));
    }

    const allResults: MediaResult[] = [];

    // 並行リクエストでバッチを処理
    const processBatch = async (batch: DeleteMediaItemRequest[]): Promise<MediaResult[]> => {
        const batchResults: MediaResult[] = [];
        let remainingItems = [...batch];
        let retryCount = 0;

        // poiIdとmediaNameのマッピングを作成（キー生成用）
        const createKey = (poiId: number, mediaName: string): string => `${poiId}:${mediaName}`;

        while (remainingItems.length > 0 && retryCount <= MAX_RETRIES) {
            try {
                // バッチAPIを呼び出す
                const res = await deleteMediaItemBulk(poiGroupID, remainingItems, oplog);

                // 成功したアイテムと失敗したアイテムを分ける
                const failedItems: DeleteMediaItemRequest[] = [];

                for (const result of res.results) {
                    const item = remainingItems.find((item) => item.poiId === result.poiId);
                    if (!item) continue;

                    const key = createKey(result.poiId, item.mediaName);

                    if (result.status === "success") {
                        // 成功したアイテムは記録（リトライで成功した場合、以前の失敗結果を上書き）
                        // 以前の失敗結果を削除
                        const existingIndex = batchResults.findIndex((r) => {
                            const existingItem = batch.find((b) => b.poiId === r.poiId);
                            return (
                                existingItem &&
                                createKey(r.poiId, existingItem.mediaName) === key &&
                                r.status === "failed"
                            );
                        });
                        if (existingIndex >= 0) {
                            batchResults.splice(existingIndex, 1);
                        }
                        // 成功結果を追加
                        batchResults.push(result);
                    } else if (result.status === "failed") {
                        if (shouldRetry(result.error)) {
                            failedItems.push(item);
                            // リトライ対象の失敗結果は、リトライが成功するまで保持しない
                            // （リトライが成功した場合は成功結果で上書きされる）
                        } else {
                            // リトライ対象外の失敗結果は即座に追加
                            batchResults.push(result);
                        }
                    }
                }

                // 失敗したアイテムがなければ終了
                if (failedItems.length === 0) {
                    break;
                }

                // リトライ上限に達したら終了
                if (retryCount >= MAX_RETRIES) {
                    // リトライ上限に達した場合、残っている失敗結果を追加
                    for (const item of failedItems) {
                        const result = res.results.find((r) => r.poiId === item.poiId);
                        if (result && result.status === "failed") {
                            batchResults.push(result);
                        }
                    }
                    break;
                }

                // 失敗したアイテムのみで再試行
                remainingItems = failedItems;
                retryCount++;

                // リトライ間隔（指数バックオフ: 1, 2, 4秒）
                const delay = Math.min(1000 * Math.pow(2, retryCount - 1), 4000);
                await sleep(delay);
            } catch (e) {
                // 504エラー（タイムアウト）の場合はリトライ対象
                const isTimeout = axios.isAxiosError(e) && e.response?.status === 504;
                if (isTimeout && retryCount < MAX_RETRIES) {
                    // タイムアウトの場合はリトライ
                    retryCount++;
                    const delay = Math.min(1000 * Math.pow(2, retryCount - 1), 4000);
                    await sleep(delay);
                    continue;
                }
                // その他のエラーまたはリトライ上限に達した場合は全アイテムを失敗として記録
                console.error("バッチ削除処理でエラーが発生しました:", e);
                for (const item of remainingItems) {
                    batchResults.push({
                        poiId: item.poiId,
                        status: "failed",
                        error: e instanceof Error ? e.message : String(e),
                    });
                }
                break;
            }
        }
        return batchResults;
    };

    // 処理開始時に進捗を0で表示
    if (onProgress) {
        onProgress(0, deleteItems.length);
    }

    // 並行リクエストを制御（最大CONCURRENT_REQUESTS並列）
    // 進捗が巻き戻らないように、完了したバッチ数で管理
    let completedBatches = 0;
    const processBatchesInParallel = async (): Promise<void> => {
        const results: MediaResult[][] = [];
        for (let i = 0; i < batches.length; i += CONCURRENT_REQUESTS) {
            const chunk = batches.slice(i, i + CONCURRENT_REQUESTS);
            const chunkResults = await Promise.all(
                chunk.map(async (batch) => {
                    const batchResults = await processBatch(batch);
                    // バッチ完了時に進捗を更新（常に増加するように）
                    completedBatches++;
                    if (onProgress) {
                        const processedCount = completedBatches * BATCH_SIZE;
                        onProgress(
                            Math.min(processedCount, deleteItems.length),
                            deleteItems.length
                        );
                    }
                    return batchResults;
                })
            );
            results.push(...chunkResults);
        }
        // 結果を統合
        for (const batchResults of results) {
            allResults.push(...batchResults);
        }
    };

    await processBatchesInParallel();

    // 最終結果をまとめる
    const successCount = allResults.filter((r) => r.status === "success").length;
    const failedCount = allResults.filter((r) => r.status === "failed").length;

    return {
        total: deleteItems.length,
        success: successCount,
        failed: failedCount,
        results: allResults,
    };
}

/**
 * GMBメディアをアップロードする
 * @returns アップロードされたメディアの取得用URL
 **/
async function uploadMedia(
    poiGroupID: number,
    file: MediaFile,
    operationLogParams: OperationLogParams
): Promise<string> {
    const url = `${apiBase}/companies/${poiGroupID}/startUpload`;
    const res = await requiredAuth<StorageGMBMediaPostResponse>("post", url, operationLogParams);
    if (res?.status == 403) {
        // 権限不足の場合requiredAuthでエラーがthrowされないのでここでthrowする
        throw new RequestError("権限不足", res);
    } else if (res?.data == null) {
        throw new RequestError("アップロードに失敗しました", res);
    }
    const putUrl = res?.data?.putUrl;
    const config = {
        headers: { "Content-Type": file.file.type },
    };
    await axios.put(putUrl, await file.load(), config);
    return res?.data?.getUrl;
}

/**
 * GMBメディアを更新する
 * @returns 更新されたメディアの情報
 **/
async function putMedia(
    poiGroupID: number,
    poiID: number,
    data: MybusinessMediaItem,
    operationLogParams: OperationLogParams
): Promise<MybusinessMediaItem> {
    const url = `${apiBase}/companies/${poiGroupID}/stores/${poiID}/gmb/media`;
    const res = await requiredAuth<MybusinessMediaItem>("put", url, operationLogParams, data);
    if (res?.status == 403) {
        throw new RequestError("権限不足", res);
    } else if (res?.data == null) {
        throw new RequestError("アップロードに失敗しました", res);
    }
    return res?.data;
}
